import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { slideUp } from '@/lib/motion';
import { phantomProps } from '@/config/phantom';
import { useTheme } from '@/contexts/ThemeContext';
import { z } from 'zod';
import DOMPurify from 'dompurify';
import toast, { Toaster } from 'react-hot-toast';
import {
  useGroups,
  useGroup,
  useCreateGroup,
  useUpdateGroup,
  useDeleteGroup,
  useAddGroupMember,
  useAllLearners,
} from '../../services/learners';
import { GroupsQueryParams } from '../../types/learner';
import { useAuth } from '../../contexts/AuthContext';
import { LearnerGroup, Learner } from '../../types/learner';
import { parseApiDate } from '../../utils/apiDates';

/**
 * Zod validation schema for group form
 *
 * Security:
 * - Client-side validation (defense-in-depth - backend also validates)
 * - Name length constraints (max 100 chars)
 * - Description length constraints (max 500 chars)
 */
const groupSchema = z.object({
  name: z
    .string()
    .min(1, 'Group name is required')
    .max(100, 'Group name must be 100 characters or less'),
  description: z
    .string()
    .max(500, 'Description must be 500 characters or less')
    .optional()
    .or(z.literal('')),
  member_ids: z.array(z.string()).optional(),
});

type GroupFormData = z.infer<typeof groupSchema>;

/**
 * Validate member IDs array (prevent injection)
 */
const validateMemberIds = (ids: string[]): boolean => {
  if (!Array.isArray(ids)) return false;
  if (ids.length === 0) return true; // Empty array is valid
  return ids.every((id) => typeof id === 'string' && id.length > 0);
};

/**
 * LearnerGroupsPage Component
 *
 * Learner Groups Management with:
 * - Group list table (name, member count, created date, actions)
 * - Create/Edit group modal
 * - Bulk member operations (add/remove)
 * - Delete confirmation modal
 * - Search and filter
 *
 * Security:
 * - Zod schema validation before submission
 * - DOMPurify sanitization on all text inputs
 * - RBAC enforcement (Owner/Admin/Trainer only)
 * - Trainers can only edit groups they created
 * - httpOnly cookies for authentication
 * - Generic error messages (no information leakage)
 */
const LearnerGroupsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, activeMembership } = useAuth();
  const { theme } = useTheme();

  // State
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);
  const [bulkGroupId, setBulkGroupId] = useState<string | null>(null);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);

  // Form state
  const [formData, setFormData] = useState<GroupFormData>({
    name: '',
    description: '',
    member_ids: [],
  });

  const [validationErrors, setValidationErrors] = useState<
    Partial<Record<keyof GroupFormData, string>>
  >({});

  // Query params
  const queryParams: GroupsQueryParams = {
    page,
    per_page: 20,
    search: search || undefined,
  };

  // React Query hooks
  const { data: groupsData, isLoading, error } = useGroups(queryParams);
  const { data: editingGroup } = useGroup(editingGroupId ?? undefined);
  const { data: allLearners, isLoading: learnersLoading } = useAllLearners();
  const createMutation = useCreateGroup();
  const updateMutation = useUpdateGroup();
  const deleteMutation = useDeleteGroup();
  const addMembersMutation = useAddGroupMember();

  // RBAC: Check if user can manage groups
  const userRole = activeMembership?.role;
  const canManageGroups =
    userRole === 'Owner' || userRole === 'Admin' || userRole === 'Trainer';

  // Populate form when editing group data loads
  useEffect(() => {
    if (editingGroup && editingGroupId) {
      setFormData({
        name: editingGroup.name,
        description: editingGroup.description || '',
        member_ids: [],
      });
    }
  }, [editingGroup, editingGroupId]);

  // RBAC: Redirect if user doesn't have permission
  if (!canManageGroups && !isLoading) {
    return (
      <div className="min-h-screen bg-background-primary p-6">
        <div className="container mx-auto max-w-7xl">
          <div className="rounded-lg bg-error-secondary p-6 text-error-primary">
            <h2 className="heading-3 mb-2">Access Denied</h2>
            <p className="body-base">
              You do not have permission to manage learner groups.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-4 px-4 py-2 rounded-full bg-accent-primary text-white hover:bg-accent-primary/90 transition-colors label-medium"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Handle input change with sanitization
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // Sanitize text input with DOMPurify
    const sanitizedValue = DOMPurify.sanitize(value, {
      ALLOWED_TAGS: [], // Strip all HTML tags
      ALLOWED_ATTR: [], // Strip all attributes
    });

    setFormData((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));

    // Clear validation error for this field
    setValidationErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  };

  // Handle member selection
  const handleMemberSelectionChange = (learnerId: string, checked: boolean) => {
    setSelectedMemberIds((prev) => {
      if (checked) {
        return [...prev, learnerId];
      } else {
        return prev.filter((id) => id !== learnerId);
      }
    });
  };

  // Validate form with Zod
  const validateForm = (): boolean => {
    const result = groupSchema.safeParse(formData);

    if (result.success) {
      setValidationErrors({});
      return true;
    }

    const errors: Partial<Record<keyof GroupFormData, string>> = {};
    result.error.issues.forEach((issue) => {
      const field = issue.path[0] as keyof GroupFormData;
      errors[field] = issue.message;
    });
    setValidationErrors(errors);
    return false;
  };

  // Open create modal
  const handleOpenCreateModal = () => {
    setEditingGroupId(null);
    setFormData({
      name: '',
      description: '',
      member_ids: [],
    });
    setValidationErrors({});
    setShowGroupModal(true);
  };

  // Open edit modal
  const handleOpenEditModal = (groupId: string) => {
    setEditingGroupId(groupId);
    setShowGroupModal(true);
  };

  // Close group modal
  const handleCloseGroupModal = () => {
    setShowGroupModal(false);
    setEditingGroupId(null);
    setFormData({
      name: '',
      description: '',
      member_ids: [],
    });
    setValidationErrors({});
  };

  // Handle form submission (create or update)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side validation
    if (!validateForm()) {
      toast.error('Please fix validation errors before submitting');
      return;
    }

    try {
      // Sanitize all text fields again before submission (defense-in-depth)
      const sanitizedData = {
        name: DOMPurify.sanitize(formData.name, {
          ALLOWED_TAGS: [],
          ALLOWED_ATTR: [],
        }),
        description: formData.description
          ? DOMPurify.sanitize(formData.description, {
              ALLOWED_TAGS: [],
              ALLOWED_ATTR: [],
            })
          : undefined,
      };

      if (editingGroupId) {
        // Update existing group
        await updateMutation.mutateAsync({
          groupId: editingGroupId!,
          data: sanitizedData,
        });
        toast.success('Group updated successfully');
      } else {
        // Create new group
        await createMutation.mutateAsync(sanitizedData);
        toast.success('Group created successfully');
      }

      handleCloseGroupModal();
    } catch (error: any) {
      console.error('Failed to save group:', error);
      // Generic error message (no information leakage)
      toast.error('Failed to save group. Please try again.');
    }
  };

  // Handle delete group
  const handleDeleteGroup = async () => {
    if (!deletingGroupId) return;

    try {
      await deleteMutation.mutateAsync(deletingGroupId!);
      toast.success('Group deleted successfully');
      setShowDeleteModal(false);
      setDeletingGroupId(null);
    } catch (error: any) {
      console.error('Failed to delete group:', error);
      // Generic error message (no information leakage)
      toast.error('Failed to delete group. Please try again.');
    }
  };

  // Open delete modal
  const handleOpenDeleteModal = (groupId: string) => {
    setDeletingGroupId(groupId);
    setShowDeleteModal(true);
  };

  // Close delete modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingGroupId(null);
  };

  // Open bulk add members modal
  const handleOpenBulkAddModal = (groupId: string) => {
    setBulkGroupId(groupId);
    setSelectedMemberIds([]);
    setShowBulkAddModal(true);
  };

  // Close bulk add members modal
  const handleCloseBulkAddModal = () => {
    setShowBulkAddModal(false);
    setBulkGroupId(null);
    setSelectedMemberIds([]);
  };

  // Handle bulk add members
  const handleBulkAddMembers = async () => {
    if (!bulkGroupId) return;

    // Validate member IDs
    if (!validateMemberIds(selectedMemberIds)) {
      toast.error('Invalid member selection');
      return;
    }

    if (selectedMemberIds.length === 0) {
      toast.error('Please select at least one member');
      return;
    }

    try {
      // Add members one at a time (API accepts single user_id)
      for (const userId of selectedMemberIds) {
        await addMembersMutation.mutateAsync({
          groupId: bulkGroupId!,
          data: { user_id: userId },
        });
      }
      toast.success(`${selectedMemberIds.length} member(s) added successfully`);
      handleCloseBulkAddModal();
    } catch (error: any) {
      console.error('Failed to add members:', error);
      // Generic error message (no information leakage)
      toast.error('Failed to add members. Please try again.');
    }
  };

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1); // Reset to first page on search
  };

  // Handle pagination
  const handlePrevPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (groupsData && page < (groupsData.pagination?.pages || 1)) setPage(page + 1);
  };

  // Error state (only after loading completes)
  if (!isLoading && error) {
    return (
      <div className="min-h-screen bg-background-primary p-6">
        <div className="container mx-auto max-w-7xl">
          <div className="rounded-lg bg-error-secondary p-6 text-error-primary">
            <h2 className="heading-3 mb-2">Error Loading Groups</h2>
            <p className="body-base">{error.message || 'Failed to load groups'}</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-4 px-4 py-2 rounded-full bg-accent-primary text-white hover:bg-accent-primary/90 transition-colors label-medium"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const groups = groupsData?.groups || [];
  const total = groupsData?.pagination?.total || 0;
  const totalPages = groupsData?.pagination?.pages || 1;

  return (
    <motion.div {...slideUp} className="min-h-screen bg-background-primary p-6">
      <Toaster
        position="top-right"
        toastOptions={{
          success: {
            duration: 3000,
            style: {
              background: 'var(--success-secondary)',
              color: 'var(--success-primary)',
            },
          },
          error: {
            duration: 5000,
            style: {
              background: 'var(--error-secondary)',
              color: 'var(--error-primary)',
            },
          },
        }}
      />

      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="heading-1 text-text-primary">Learner Groups</h1>
            <p className="body-base text-text-secondary mt-2">
              Manage learner groups and memberships
            </p>
          </div>
          {canManageGroups && (
            <button
              onClick={handleOpenCreateModal}
              className="px-4 py-2 rounded-full bg-accent-primary text-white hover:bg-accent-primary/90 transition-colors label-medium"
              data-testid="create-group-btn"
            >
              Create Group
            </button>
          )}
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search groups..."
              className="flex-1 px-4 py-2 rounded-lg border border-border-primary bg-background-secondary text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent"
              data-testid="search-input"
            />
            <button
              type="submit"
              className="px-4 py-2 rounded-full bg-accent-primary text-white hover:bg-accent-primary/90 transition-colors label-medium"
            >
              Search
            </button>
          </div>
        </form>

        {/* Groups Table */}
        <phantom-ui {...phantomProps(theme === 'dark', isLoading)}>
        <div className="rounded-lg border border-border-primary bg-background-secondary overflow-hidden">
          {isLoading ? (
            <div className="p-6 space-y-3">
              <div className="h-12 rounded" />
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-16 rounded" />
              ))}
            </div>
          ) : groups.length === 0 ? (
            <div className="p-12 text-center">
              <div className="mb-4">
                <svg
                  className="mx-auto h-12 w-12 text-text-tertiary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <h3 className="heading-3 text-text-primary mb-2">No Groups Found</h3>
              <p className="body-base text-text-tertiary mb-4">
                {search
                  ? 'Try adjusting your search criteria'
                  : 'Get started by creating your first group'}
              </p>
              {!search && canManageGroups && (
                <button
                  onClick={handleOpenCreateModal}
                  className="px-4 py-2 rounded-full bg-accent-primary text-white hover:bg-accent-primary/90 transition-colors label-medium"
                >
                  Create Group
                </button>
              )}
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-background-tertiary">
                  <tr>
                    <th className="px-6 py-3 text-left label-small text-text-secondary">
                      Group Name
                    </th>
                    <th className="px-6 py-3 text-left label-small text-text-secondary">
                      Members
                    </th>
                    <th className="px-6 py-3 text-left label-small text-text-secondary">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right label-small text-text-secondary">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-primary">
                  {groups.map((group: LearnerGroup) => {
                    // Check if trainer can edit this group
                    const canEdit =
                      userRole !== 'Trainer' ||
                      (group as any).created_by === user?.id;

                    return (
                      <tr
                        key={group.id}
                        className="hover:bg-background-tertiary transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div>
                            <p className="body-medium text-text-primary">{group.name}</p>
                            {group.description && (
                              <p className="body-small text-text-tertiary mt-1">
                                {group.description}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-3 py-1 rounded-full bg-accent-primary/10 text-accent-primary label-small">
                            {group.members_count} member(s)
                          </span>
                        </td>
                        <td className="px-6 py-4 body-small text-text-secondary">
                          {parseApiDate(group.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            {canEdit && (
                              <>
                                <button
                                  onClick={() => handleOpenBulkAddModal(group.id)}
                                  className="px-3 py-1 rounded-full border border-border-primary bg-background-secondary text-text-primary hover:bg-background-tertiary transition-colors label-small"
                                  data-testid={`add-members-btn-${group.id}`}
                                >
                                  Add Members
                                </button>
                                <button
                                  onClick={() => handleOpenEditModal(group.id)}
                                  className="px-3 py-1 rounded-full border border-border-primary bg-background-secondary text-text-primary hover:bg-background-tertiary transition-colors label-small"
                                  data-testid={`edit-btn-${group.id}`}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleOpenDeleteModal(group.id)}
                                  className="px-3 py-1 rounded-full bg-error-secondary text-error-primary hover:bg-error-primary hover:text-white transition-colors label-small"
                                  data-testid={`delete-btn-${group.id}`}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-border-primary flex items-center justify-between">
                  <p className="body-small text-text-tertiary">
                    Page {page} of {totalPages} ({total} total)
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={page === 1}
                      className="px-3 py-1 rounded-full border border-border-primary bg-background-secondary text-text-primary hover:bg-background-tertiary transition-colors label-small disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={handleNextPage}
                      disabled={page === totalPages}
                      className="px-3 py-1 rounded-full border border-border-primary bg-background-secondary text-text-primary hover:bg-background-tertiary transition-colors label-small disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        </phantom-ui>
      </div>

      {/* Create/Edit Group Modal */}
      {showGroupModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={handleCloseGroupModal}
        >
          <div
            className="bg-background-secondary rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="heading-3 text-text-primary mb-4">
              {editingGroupId ? 'Edit Group' : 'Create Group'}
            </h3>

            <form onSubmit={handleSubmit}>
              {/* Group Name */}
              <div className="mb-4">
                <label
                  htmlFor="name"
                  className="block label-medium text-text-primary mb-2"
                >
                  Group Name <span className="text-error-primary">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    validationErrors.name
                      ? 'border-error-primary'
                      : 'border-border-primary'
                  } bg-background-primary text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent`}
                  placeholder="Enter group name"
                  data-testid="group-name-input"
                />
                {validationErrors.name && (
                  <p className="mt-1 body-small text-error-primary">
                    {validationErrors.name}
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="mb-6">
                <label
                  htmlFor="description"
                  className="block label-medium text-text-primary mb-2"
                >
                  Description (optional)
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    validationErrors.description
                      ? 'border-error-primary'
                      : 'border-border-primary'
                  } bg-background-primary text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent resize-none`}
                  placeholder="Enter group description (max 500 characters)"
                  data-testid="group-description-input"
                />
                {validationErrors.description && (
                  <p className="mt-1 body-small text-error-primary">
                    {validationErrors.description}
                  </p>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseGroupModal}
                  className="px-4 py-2 rounded-full border border-border-primary bg-background-tertiary text-text-primary hover:bg-border-primary transition-colors label-medium"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 rounded-full bg-accent-primary text-white hover:bg-accent-primary/90 transition-colors label-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="save-group-btn"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingGroupId && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={handleCloseDeleteModal}
        >
          <div
            className="bg-background-secondary rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="heading-3 text-text-primary mb-4">Delete Group</h3>
            <p className="body-base text-text-secondary mb-6">
              Are you sure you want to delete this group? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={handleCloseDeleteModal}
                className="px-4 py-2 rounded-full border border-border-primary bg-background-tertiary text-text-primary hover:bg-border-primary transition-colors label-medium"
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteGroup}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 rounded-full bg-error-primary text-white hover:bg-error-primary/90 transition-colors label-medium disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-delete-btn"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Add Members Modal */}
      {showBulkAddModal && bulkGroupId && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={handleCloseBulkAddModal}
        >
          <div
            className="bg-background-secondary rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="heading-3 text-text-primary mb-4">Add Members to Group</h3>

            <phantom-ui {...phantomProps(theme === 'dark', learnersLoading)}>
            {learnersLoading ? (
              <div className="space-y-3 mb-6">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-12 rounded" />
                ))}
              </div>
            ) : allLearners && allLearners.length > 0 ? (
              <div className="mb-6 max-h-96 overflow-y-auto">
                <div className="space-y-2">
                  {allLearners.map((learner: Learner) => (
                    <label
                      key={learner.id}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-background-tertiary cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedMemberIds.includes(learner.id)}
                        onChange={(e) =>
                          handleMemberSelectionChange(learner.id, e.target.checked)
                        }
                        className="w-5 h-5 rounded border-border-primary text-accent-primary focus-visible:ring-accent-primary focus:ring-offset-0"
                        data-testid={`member-checkbox-${learner.id}`}
                      />
                      <div className="flex-1">
                        <p className="body-medium text-text-primary">
                          {learner.first_name} {learner.last_name}
                        </p>
                        <p className="body-small text-text-tertiary">{learner.email}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <p className="body-base text-text-tertiary mb-6">
                No learners available to add.
              </p>
            )}
            </phantom-ui>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={handleCloseBulkAddModal}
                className="px-4 py-2 rounded-full border border-border-primary bg-background-tertiary text-text-primary hover:bg-border-primary transition-colors label-medium"
                disabled={addMembersMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAddMembers}
                disabled={
                  addMembersMutation.isPending || selectedMemberIds.length === 0
                }
                className="px-4 py-2 rounded-full bg-accent-primary text-white hover:bg-accent-primary/90 transition-colors label-medium disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-add-members-btn"
              >
                {addMembersMutation.isPending
                  ? 'Adding...'
                  : `Add ${selectedMemberIds.length} Member(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default LearnerGroupsPage;
